import { createTheme, responsiveFontSizes } from '@mui/material/styles';

/**
 * Transforms a hex color into a pastel color.
 * @param {string} hex - The hex color to transform.
 * @returns {string} - The pastel color in hex format.
 */
export function hexToPastel(hex) {
    // Ensure the hex is valid
    if (!/^#([0-9A-F]{3}){1,2}$/i.test(hex)) {
        throw new Error('Invalid hex color');
    }

    // Convert the hex to RGB
    let r = 0,
        g = 0,
        b = 0;
    if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
        r = parseInt(hex[1] + hex[2], 16);
        g = parseInt(hex[3] + hex[4], 16);
        b = parseInt(hex[5] + hex[6], 16);
    }

    // Mix the color with white
    r = Math.floor((r + 255) / 2);
    g = Math.floor((g + 255) / 2);
    b = Math.floor((b + 255) / 2);

    // Convert the pastel color back to hex
    const pastelHex = `#${((1 << 24) + (r << 16) + (g << 8) + b)
        .toString(16)
        .slice(1)
        .toUpperCase()}`;

    return pastelHex;
}

let TailwindTheme = createTheme({
    // Theme customization goes here as usual, including tonalOffset and/or
    // contrastThreshold as the augmentColor() function relies on these
});

TailwindTheme = createTheme({
    palette: {
        type: 'light',
        background: {
            default: "#cacadd",
        },
        paper: {
            main: "#e9e9f1",
        },
        primary: TailwindTheme.palette.augmentColor({
            color: { main: '#1a242d' },
            name: 'primary',
        }),
        secondary: TailwindTheme.palette.augmentColor({
            color: { main: '#FF0266' },
            name: 'secondary',
        }),
        success: TailwindTheme.palette.augmentColor({
            color: { main: '#46bf83' },
            name: 'success',
        }),
        info: TailwindTheme.palette.augmentColor({
            color: { main: '#0070B7' },
            name: 'info',
        }),
        warning: TailwindTheme.palette.augmentColor({
            color: { main: '#ffa400' },
            name: 'warning',
        }),
        error: TailwindTheme.palette.augmentColor({
            color: { main: '#fe2d38' },
            name: 'error',
        }),
        slate: TailwindTheme.palette.augmentColor({
            color: { main: '#64748b' },
            name: 'slate',
        }),
        gray: TailwindTheme.palette.augmentColor({
            color: { main: '#6b7280' },
            name: 'gray',
        }),
        zinc: TailwindTheme.palette.augmentColor({
            color: { main: '#71717a' },
            name: 'zinc',
        }),
        neutral: TailwindTheme.palette.augmentColor({
            color: { main: '#737373' },
            name: 'neutral',
        }),
        stone: TailwindTheme.palette.augmentColor({
            color: { main: '#78716c' },
            name: 'stone',
        }),
        red: TailwindTheme.palette.augmentColor({
            color: { main: '#ef4444' },
            name: 'red',
        }),
        orange: TailwindTheme.palette.augmentColor({
            color: { main: '#f97316' },
            name: 'orange',
        }),
        amber: TailwindTheme.palette.augmentColor({
            color: { main: '#f59e0b' },
            name: 'amber',
        }),
        yellow: TailwindTheme.palette.augmentColor({
            color: { main: '#eab308' },
            name: 'yellow',
        }),
        lime: TailwindTheme.palette.augmentColor({
            color: { main: '#84cc16' },
            name: 'lime',
        }),
        green: TailwindTheme.palette.augmentColor({
            color: { main: '#22c55e' },
            name: 'green',
        }),
        emerald: TailwindTheme.palette.augmentColor({
            color: { main: '#10b981' },
            name: 'emerald',
        }),
        teal: TailwindTheme.palette.augmentColor({
            color: { main: '#14b8a6' },
            name: 'teal',
        }),
        cyan: TailwindTheme.palette.augmentColor({
            color: { main: '#06b6d4' },
            name: 'cyan',
        }),
        sky: TailwindTheme.palette.augmentColor({
            color: { main: '#0ea5e9' },
            name: 'sky',
        }),
        blue: TailwindTheme.palette.augmentColor({
            color: { main: '#3b82f6' },
            name: 'blue',
        }),
        indigo: TailwindTheme.palette.augmentColor({
            color: { main: '#6366f1' },
            name: 'indigo',
        }),
        violet: TailwindTheme.palette.augmentColor({
            color: { main: '#8b5cf6' },
            name: 'violet',
        }),
        purple: TailwindTheme.palette.augmentColor({
            color: { main: '#a855f7' },
            name: 'purple',
        }),
        fuchsia: TailwindTheme.palette.augmentColor({
            color: { main: '#d946ef' },
            name: 'fuchsia',
        }),
        pink: TailwindTheme.palette.augmentColor({
            color: { main: '#ec4899' },
            name: 'pink',
        }),
        rose: TailwindTheme.palette.augmentColor({
            color: { main: '#f43f5e' },
            name: 'rose',
        }),
        pastelYellow: TailwindTheme.palette.augmentColor({
            color: { main: hexToPastel('#fde047') },
            name: 'pastelYellow',
        }),
        pastelSky: TailwindTheme.palette.augmentColor({
            color: { main: hexToPastel('#0ea5e9') },
            name: 'pastelSky',
        }),
        pastelEmerald: TailwindTheme.palette.augmentColor({
            color: { main: hexToPastel('#10b981') },
            name: 'pastelEmerald',
        }),
        pastelViolet: TailwindTheme.palette.augmentColor({
            color: { main: hexToPastel('#8b5cf6') },
            name: 'pastelViolet',
        }),
    },
});

// TODO: add the missing colors in zippeditoolsjs library
const addMissingStyles = (inputTheme) => (createTheme({
    ...inputTheme,
    palette: {
        ...inputTheme.palette,
        ...TailwindTheme.palette,
    },
}))

TailwindTheme = responsiveFontSizes(TailwindTheme);

export { addMissingStyles };
